import { config, resetGameState } from '@js/state';
import { TUTORIAL_ROUND } from '@utils/roundGenerator';

/**
 * TutorialScene = Q1 of the Pizza game.
 *
 * Fully-guided 5-beat storyboard (matches Figma node 1741:506):
 *   1. Order intro     — dim + "FIRST Order / Mushroom Pizza" (order-text-tutorial)
 *   2. Pizza entry     — pizza fades in, recipe bar slides up, text-2
 *   3. Counting hint   — recipe-bar pulse to draw the eye
 *   4. Tray entry      — tray + (parked above) box slide down on the belt,
 *                        text-3 ("Add some toppings…")
 *   5. CTA guide       — after TARGET_PICKS, text-4 ("Confirm your answer")
 *                        + CTA pulse, evaluate enabled.
 *
 * Correct evaluate → belt-driven box reveal (tray exits bottom, box drops
 * into view) → pizza flies in → top lid closes → box slides off right →
 * handoff to GameScene Q2.
 *
 * All visuals + master logic ported from GameScene so the tutorial reads
 * as the "first round" of the same set:
 *   • PIZZA_DISPLAY_SIZE 440 (was 360)
 *   • CONVEYOR_DISPLAY_W 450 (was 360)
 *   • topLidAnim + pizza-bottom-plate box (was pizzaBoxAnim + bottom-box)
 *   • pickPizzaSlot best-candidate placement with centroid balance
 *   • Adaptive tray grid using trayInnerW/H + per-cell item scale
 */

// ─── Layout constants (mirror GameScene) ────────────────────────────────
const STAGE_W = 1280;
const STAGE_H = 720;

const PIZZA_DISPLAY_SIZE = 440;
const PIZZA_X = 936;
const PIZZA_Y = 396;

const TRAY_DISPLAY_W = 340;
const TRAY_DISPLAY_H = 380;
const TRAY_HOME_X = 156 + TRAY_DISPLAY_W / 2 - 5; // 321 (matches GameScene)
const TRAY_HOME_Y = 120 + TRAY_DISPLAY_H / 2;     // 310
const TRAY_OFFSCREEN_Y_TOP    = -TRAY_DISPLAY_H;        // -380
const TRAY_OFFSCREEN_Y_BOTTOM = 720 + TRAY_DISPLAY_H;   // 1100

const BELT_SCROLL_SPEED = 320;

const CONVEYOR_CENTER_X = 320;
const CONVEYOR_TOP_Y = STAGE_H / 2;
const CONVEYOR_DISPLAY_W = 450;
const CONVEYOR_DISPLAY_H = STAGE_H;

const RECIPE_BAR_W = 645;
const RECIPE_BAR_H = 152;
const RECIPE_BAR_X = 30 + RECIPE_BAR_W / 2; // 352.5
const RECIPE_BAR_Y = 573 + RECIPE_BAR_H / 2; // 649

const CHECK_BTN_X = 620;
const CHECK_BTN_Y = 631;
const CHECK_BTN_W = 80;
const CHECK_BTN_H = 78;

// Pizza box (rides the belt above the tray; revealed by belt-scroll).
const BOX_HOME_X = 320;
const BOX_HOME_Y = 280;
const BOX_SCALE  = 1.05;
const BOX_ORIGIN = { x: 0.675, y: 0.695 };
const BOX_REST_Y = BOX_HOME_Y - (TRAY_OFFSCREEN_Y_BOTTOM - TRAY_HOME_Y); // -510

const FIGMA_EXPORT_SCALE = 0.5;

const TARGET_PICKS = TUTORIAL_ROUND.answer; // 8

// The dim overlay stays up for beats 1-4 — it mutes the background.
// Gameplay elements (pizza, tray, recipe bar, buttons) sit ABOVE the dim
// so they remain bright; only the bg + conveyor get muted underneath.
const D = {
  bg: 0, conveyor: 5,
  dim: 8,
  tray: 10, trayIngredient: 15,
  pizza: 20, pizzaIngredient: 25,
  recipeBar: 30,
  buttons: 35,
  pointer: 40,
  overlay: 50,
  confetti: 60,
  hud: 70,
  box: 80,
};

export default class TutorialScene extends Phaser.Scene {
  constructor() {
    super({ key: 'TutorialScene' });
  }

  create() {
    resetGameState();
    this.cameras.main.setBackgroundColor('#FFE9CC');
    this.cameras.main.fadeIn(400, 0, 0, 0);

    // ─── Static background ────────────────────────────────────────────
    if (this.textures.exists('game-scene-bg')) {
      this.add.image(STAGE_W / 2, STAGE_H / 2, 'game-scene-bg')
        .setDisplaySize(STAGE_W, STAGE_H).setDepth(D.bg);
    }
    if (this.textures.exists('conveyor-belt-1')) {
      const tex = this.textures.get('conveyor-belt-1').getSourceImage();
      this.conveyor = this.add
        .tileSprite(CONVEYOR_CENTER_X, CONVEYOR_TOP_Y, CONVEYOR_DISPLAY_W, CONVEYOR_DISPLAY_H, 'conveyor-belt-1')
        .setOrigin(0.5).setDepth(D.conveyor);
      // Uniform tileScale — no x-shrink (matches GameScene).
      this.beltTileScale = CONVEYOR_DISPLAY_W / tex.width;
      this.conveyor.setTileScale(this.beltTileScale, this.beltTileScale);
    }

    // ─── Pizza (hidden at start; fades in during beat 2) ──────────────
    if (this.textures.exists('pizza')) {
      this.pizza = this.add.image(PIZZA_X, PIZZA_Y, 'pizza')
        .setDisplaySize(PIZZA_DISPLAY_SIZE, PIZZA_DISPLAY_SIZE)
        .setDepth(D.pizza).setAlpha(0);
    } else {
      this.pizza = this.add.circle(PIZZA_X, PIZZA_Y, PIZZA_DISPLAY_SIZE / 2, 0xF4C26B)
        .setStrokeStyle(6, 0x8A4A1F).setDepth(D.pizza).setAlpha(0);
    }
    this.placedIngredients = [];

    // ─── Recipe bar (offscreen-below at start) ────────────────────────
    // Capture each sprite's "home" Y from buildRecipeBar so beat 2 can
    // tween each one back to its own anchor. The pill and the text
    // overlay sit at different Ys (text is TEXT_PAD_Y above the pill
    // centre), so they can't share a single delta.
    this.buildRecipeBar();
    this.recipeBarHomeY = this.recipeBar.y;
    this.recipeTextHomeY = this.recipeText ? this.recipeText.y : null;
    this.recipeBar.y = STAGE_H + RECIPE_BAR_H;
    if (this.recipeText) this.recipeText.y = STAGE_H + RECIPE_BAR_H;

    // ─── Evaluate button (hidden at start; revealed in beat 5) ────────
    this.evaluateBtn = this.buildEvaluateButton();
    this.evaluateBtn.setVisible(false).disableInteractive();

    // ─── Tutorial pointer ("tap here" finger) ─────────────────────────
    this.pointer = null;
    this.pointerTween = null;

    // ─── Anims used in the celebration ────────────────────────────────
    this.setupTopLidAnim();
    this.setupConfetti();

    // ─── Per-round state ──────────────────────────────────────────────
    this.pickedCount = 0;
    this.evaluating = false;
    this.trayIngredients = [];
    this.tray = null;
    this.boxBottom = null;
    this.boxTop = null;
    this.beltMoving = false;

    // Kick off the storyboard.
    this.playBeat1_OrderIntro();
  }

  update(_t, dt) {
    if (this.conveyor && this.beltMoving) {
      const pxPerSec = BELT_SCROLL_SPEED / (this.beltTileScale || 1);
      this.conveyor.tilePositionY -= (dt / 1000) * pxPerSec;
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // BEAT 1 — Order intro
  // ═══════════════════════════════════════════════════════════════════
  playBeat1_OrderIntro() {
    this.dim = this.add
      .image(0, 0, 'grey-overlay')
      .setOrigin(0, 0)
      .setDisplaySize(STAGE_W, STAGE_H)
      .setAlpha(0)
      .setDepth(D.dim);

    const textKey = `order-text-${TUTORIAL_ROUND.id}`;
    let intro = null;
    if (this.textures.exists(textKey)) {
      intro = this.add.image(159, 385, textKey)
        .setOrigin(0, 0.5).setScale(FIGMA_EXPORT_SCALE)
        .setAlpha(0).setDepth(D.overlay);
    }

    this.tweens.add({ targets: this.dim, alpha: 1, duration: 240, ease: 'Sine.easeOut' });
    if (intro) {
      this.tweens.add({
        targets: intro, alpha: 1, y: '-=10',
        duration: 300, delay: 80, ease: 'Cubic.easeOut',
      });
    }

    this.time.delayedCall(2000, () => {
      if (intro) {
        this.tweens.add({
          targets: intro, alpha: 0, duration: 350, ease: 'Sine.easeIn',
          onComplete: () => { intro.destroy(); this.playBeat2_PizzaEntry(); },
        });
      } else {
        this.playBeat2_PizzaEntry();
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // BEAT 2 — Pizza fades in, recipe bar slides up, text-2
  // ═══════════════════════════════════════════════════════════════════
  playBeat2_PizzaEntry() {
    const homeScale = this.pizza.scale;
    this.pizza.setScale(homeScale * 0.85);
    this.tweens.add({
      targets: this.pizza,
      alpha: 1, scale: homeScale,
      duration: 450, ease: 'Back.easeOut',
    });

    // Recipe bar (pill + text overlay) slide up. They have different
    // home Y positions (text is TEXT_PAD_Y above the pill), so each
    // tweens to its own captured home Y rather than sharing a delta.
    this.tweens.add({
      targets: this.recipeBar,
      y: this.recipeBarHomeY,
      duration: 600, ease: 'Cubic.easeOut',
    });
    if (this.recipeText) {
      this.tweens.add({
        targets: this.recipeText,
        y: this.recipeTextHomeY,
        duration: 600, ease: 'Cubic.easeOut',
      });
    }

    const text2 = this.showOverlayText('tutorial-text-2', 318, 393);

    this.time.delayedCall(2200, () => {
      if (text2) {
        this.tweens.add({
          targets: text2, alpha: 0, duration: 350, ease: 'Sine.easeIn',
          onComplete: () => { text2.destroy(); this.playBeat3_CountingHint(); },
        });
      } else {
        this.playBeat3_CountingHint();
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // BEAT 3 — Counting hint (pulse the recipe bar)
  // ═══════════════════════════════════════════════════════════════════
  playBeat3_CountingHint() {
    const home = this.recipeBarHomeScale;
    const pulseTargets = [this.recipeBar];
    if (this.recipeText) pulseTargets.push(this.recipeText);
    this.tweens.add({
      targets: pulseTargets,
      scaleX: { from: home, to: home * 1.04 },
      scaleY: { from: home, to: home * 1.04 },
      yoyo: true, repeat: 2, duration: 240, ease: 'Sine.easeInOut',
      onComplete: () => {
        this.recipeBar.setScale(home);
        if (this.recipeText) this.recipeText.setScale(FIGMA_EXPORT_SCALE);
        this.playBeat4_TrayEntry();
      },
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // BEAT 4 — Tray + parked box slide down on belt, text-3 visible
  // ═══════════════════════════════════════════════════════════════════
  playBeat4_TrayEntry() {
    const text3 = this.showOverlayText('tutorial-text-3', 318, 393);

    const READ_TIME = 2600;
    const FADE_MS = 350;
    const HIDE_BEFORE_SETTLE = 500;

    this.time.delayedCall(READ_TIME, () => {
      const startY = TRAY_OFFSCREEN_Y_TOP;
      const slideMs = ((TRAY_HOME_Y - startY) / BELT_SCROLL_SPEED) * 1000;
      const fadeStart = Math.max(0, slideMs - HIDE_BEFORE_SETTLE - FADE_MS);

      this.spawnTrayWithMushrooms(() => {
        this.showPointerOverNextMushroom();
      });

      if (text3) {
        this.time.delayedCall(fadeStart, () => {
          this.tweens.add({
            targets: text3, alpha: 0, duration: FADE_MS, ease: 'Sine.easeIn',
            onComplete: () => text3.destroy(),
          });
        });
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // BEAT 5 — All TARGET_PICKS mushrooms placed → CTA guide
  // ═══════════════════════════════════════════════════════════════════
  playBeat5_CTAGuide() {
    this.evaluateBtn.setVisible(true).setInteractive({ useHandCursor: true });
    if (this.evaluatePulse) this.evaluatePulse.resume();
    this.text4 = this.showOverlayText('tutorial-text-4', 841, 637);
  }

  // ═══════════════════════════════════════════════════════════════════
  // Tutorial pointer ("tap the mushrooms" finger)
  // Hovers over the next un-picked tray mushroom with a tap-bounce.
  // ═══════════════════════════════════════════════════════════════════
  showPointerOverNextMushroom() {
    const target = this.trayIngredients.find(s => !s.picked);
    if (!target || this.pickedCount >= TARGET_PICKS) return;

    if (!this.pointer) {
      if (!this.textures.exists('tutorial-pointer')) return;
      this.pointer = this.add
        .image(0, 0, 'tutorial-pointer')
        .setOrigin(0.18, 0.08)
        .setScale(0.3)
        .setDepth(D.pointer);
    }
    this.pointer.setPosition(target.x + 12, target.y + 10);
    this.pointer.setVisible(true).setAlpha(1);

    if (this.pointerTween) this.pointerTween.stop();
    this.pointerTween = this.tweens.add({
      targets: this.pointer,
      scale: { from: 0.3, to: 0.24 },
      yoyo: true, repeat: -1, duration: 420, ease: 'Sine.easeInOut',
    });
  }

  hidePointer() {
    if (this.pointerTween) { this.pointerTween.stop(); this.pointerTween = null; }
    if (this.pointer) {
      this.pointer.setVisible(false);
      this.pointer.setScale(0.3);
    }
  }

  destroyPointer() {
    this.hidePointer();
    if (this.pointer) { this.pointer.destroy(); this.pointer = null; }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Overlay text helper
  // ═══════════════════════════════════════════════════════════════════
  showOverlayText(key, cx, cy) {
    if (!this.textures.exists(key)) return null;
    const img = this.add.image(cx, cy, key)
      .setOrigin(0.5).setScale(FIGMA_EXPORT_SCALE).setAlpha(0).setDepth(D.overlay);
    this.tweens.add({
      targets: img, alpha: 1, duration: 280, ease: 'Sine.easeOut',
    });
    return img;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Recipe bar — empty pill + text overlay (same pattern as GameScene)
  // ═══════════════════════════════════════════════════════════════════
  buildRecipeBar() {
    const pillKey = this.textures.exists('recipe-bar-pill') ? 'recipe-bar-pill'
                  : (this.textures.exists(`recipe-bar-${TUTORIAL_ROUND.id}`)
                      ? `recipe-bar-${TUTORIAL_ROUND.id}` : null);
    if (pillKey) {
      this.recipeBar = this.add.image(RECIPE_BAR_X, RECIPE_BAR_Y, pillKey)
        .setOrigin(0.5)
        .setDisplaySize(RECIPE_BAR_W, RECIPE_BAR_H)
        .setDepth(D.recipeBar);
      this.recipeBarHomeScale = this.recipeBar.scaleX;
    } else {
      const g = this.add.graphics().setDepth(D.recipeBar);
      g.fillStyle(0xFFC8A8, 1);
      g.fillRoundedRect(
        RECIPE_BAR_X - RECIPE_BAR_W / 2, RECIPE_BAR_Y - RECIPE_BAR_H / 2,
        RECIPE_BAR_W, RECIPE_BAR_H, RECIPE_BAR_H / 2,
      );
      this.recipeBar = g;
      this.recipeBarHomeScale = 1;
    }

    // Per-round text overlay (mirrors GameScene.setRecipeBarForRound).
    // Tutorial-only: TEXT_PAD_LEFT is -15 vs GameScene's 115 — the
    // longer "Mushrooms" label needs to sit further left to look centred.
    const textKey = `recipe-text-${TUTORIAL_ROUND.id}`;
    if (this.textures.exists(textKey)) {
      const TEXT_PAD_LEFT = 100;
      const TEXT_PAD_Y    = -15;
      const textX = RECIPE_BAR_X - RECIPE_BAR_W / 2 + TEXT_PAD_LEFT;
      const textY = RECIPE_BAR_Y + TEXT_PAD_Y;
      this.recipeText = this.add.image(textX, textY, textKey)
        .setOrigin(0, 0.5)
        .setScale(FIGMA_EXPORT_SCALE)
        .setDepth(D.recipeBar + 1);
    } else {
      this.recipeText = null;
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Evaluate (green check) button
  // ═══════════════════════════════════════════════════════════════════
  buildEvaluateButton() {
    const key = this.textures.exists('check-button') ? 'check-button' : null;
    let btn;
    if (key) {
      btn = this.add.image(CHECK_BTN_X, CHECK_BTN_Y, key)
        .setOrigin(0.5).setDisplaySize(CHECK_BTN_W, CHECK_BTN_H)
        .setDepth(D.buttons + 1);
      const home = btn.scaleX;
      btn.on('pointerover', () => btn.setScale(home * 1.06));
      btn.on('pointerout',  () => btn.setScale(home));
      btn.on('pointerdown', () => btn.setScale(home * 0.94));
      btn.on('pointerup', () => { btn.setScale(home); this.onEvaluate(); });
      this.evaluateBtnHomeScale = home;
    } else {
      btn = this.add.circle(CHECK_BTN_X, CHECK_BTN_Y, CHECK_BTN_W / 2, 0x6BCF7F)
        .setStrokeStyle(3, 0x2E8B57).setDepth(D.buttons + 1);
      btn.on('pointerup', () => this.onEvaluate());
      this.evaluateBtnHomeScale = 1;
    }

    const h = this.evaluateBtnHomeScale;
    this.evaluatePulse = this.tweens.add({
      targets: btn, scale: { from: h, to: h * 1.06 },
      yoyo: true, repeat: -1, duration: 600, paused: true,
    });
    return btn;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Tray + mushrooms (adaptive grid, port of GameScene.spawnTrayWithIngredients)
  // ═══════════════════════════════════════════════════════════════════
  get trayInnerW() { return 280; }
  get trayInnerH() { return 310; }

  spawnTrayWithMushrooms(onSettled) {
    const startY = TRAY_OFFSCREEN_Y_TOP;
    const tray = this.textures.exists('tray')
      ? this.add.image(TRAY_HOME_X, startY, 'tray')
          .setDisplaySize(TRAY_DISPLAY_W, TRAY_DISPLAY_H).setDepth(D.tray)
      : this.add.rectangle(TRAY_HOME_X, startY, TRAY_DISPLAY_W, TRAY_DISPLAY_H, 0xE8D8C0)
          .setStrokeStyle(4, 0x8A4A1F).setDepth(D.tray);
    this.tray = tray;

    // Pizza box parked off-screen above the tray on the same belt.
    const trayTravel = TRAY_HOME_Y - startY;
    const boxStartY = BOX_REST_Y - trayTravel;
    this.boxBottom = this.add
      .image(BOX_HOME_X, boxStartY, 'pizza-bottom-plate')
      .setOrigin(BOX_ORIGIN.x, BOX_ORIGIN.y)
      .setScale(BOX_SCALE)
      .setDepth(D.tray - 1);
    this.boxTop = this.add
      .sprite(BOX_HOME_X, boxStartY, 'topLidAnim', 'topLidAnim0.png')
      .setOrigin(BOX_ORIGIN.x, BOX_ORIGIN.y)
      .setScale(BOX_SCALE)
      .setDepth(D.tray - 1);

    // Adaptive grid (4×4 for total=16) inside the tray.
    const positions = this.computeTrayGrid(TUTORIAL_ROUND.total);
    const itemKey  = 'ing-mushroom-item';
    const emptyKey = 'ing-mushroom-empty';

    positions.forEach(([dx, dy, cellSize]) => {
      const x = TRAY_HOME_X + dx;
      const y = startY + dy;
      const itemSize = Math.min(64, Math.max(32, (cellSize || 56) * 0.78));
      const sprite = this.textures.exists(itemKey)
        ? this.add.image(x, y, itemKey)
        : this.add.circle(x, y, 22, 0xCC6633);
      if (sprite.setDisplaySize) sprite.setDisplaySize(itemSize, itemSize);
      sprite.trayItemSize = itemSize;
      sprite.setDepth(D.trayIngredient);
      sprite.picked = false;
      sprite.setInteractive({ useHandCursor: true });
      sprite.on('pointerup', () => this.onPick(sprite, itemKey, emptyKey));
      this.trayIngredients.push(sprite);
    });

    const distance = trayTravel;
    const duration = (distance / BELT_SCROLL_SPEED) * 1000;
    this.beltMoving = true;
    this.tweens.add({
      targets: [this.tray, ...this.trayIngredients, this.boxBottom, this.boxTop],
      y: '+=' + distance, duration, ease: 'Linear',
      onComplete: () => {
        this.beltMoving = false;
        if (onSettled) onSettled();
      },
    });
  }

  computeTrayGrid(total) {
    const SHAPES = {
      6:  { rows: 2, cols: 3 },
      8:  { rows: 2, cols: 4 },
      9:  { rows: 3, cols: 3 },
      10: { rows: 2, cols: 5 },
      12: { rows: 3, cols: 4 },
      15: { rows: 3, cols: 5 },
      16: { rows: 4, cols: 4 },
    };
    const shape = SHAPES[total] || (() => {
      const cols = Math.ceil(Math.sqrt(total));
      return { rows: Math.ceil(total / cols), cols };
    })();
    const { rows, cols } = shape;
    const cellW = this.trayInnerW / cols;
    const cellH = this.trayInnerH / rows;
    const positions = [];
    for (let i = 0; i < total; i++) {
      const r = Math.floor(i / cols);
      const c = i % cols;
      const dx = (c - (cols - 1) / 2) * cellW;
      const dy = (r - (rows - 1) / 2) * cellH;
      positions.push([dx, dy, Math.min(cellW, cellH)]);
    }
    return positions;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Pick handler (mirrors GameScene.onTrayIngredientTap)
  // ═══════════════════════════════════════════════════════════════════
  onPick(sprite, itemKey, emptyKey) {
    if (this.evaluating || sprite.picked) return;
    if (this.pickedCount >= TARGET_PICKS) return; // cap at TARGET_PICKS
    sprite.picked = true;
    sprite.disableInteractive();

    if (this.textures.exists(emptyKey)) {
      sprite.setTexture(emptyKey);
      const sz = sprite.trayItemSize || 44;
      sprite.setDisplaySize(sz, sz);
    } else {
      sprite.setTint(0xAAAAAA);
    }

    // pickPizzaSlot — best-candidate sampling with centroid balance.
    const slot = this.pickPizzaSlot();
    const flying = this.textures.exists(itemKey)
      ? this.add.image(sprite.x, sprite.y, itemKey)
      : this.add.circle(sprite.x, sprite.y, 22, 0xCC6633);
    if (flying.setDisplaySize) flying.setDisplaySize(48, 48);
    flying.setDepth(D.pizzaIngredient);
    this.placedIngredients.push(flying);
    this.pickedCount++;

    this.tweens.add({
      targets: flying,
      x: slot.x, y: slot.y, angle: slot.angle,
      duration: 380, ease: 'Cubic.easeOut',
      onComplete: () => {
        if (this.pickedCount === TARGET_PICKS) {
          this.trayIngredients.forEach(s => { if (!s.picked) s.disableInteractive(); });
          this.destroyPointer();
          this.playBeat5_CTAGuide();
        }
      },
    });

    if (this.pickedCount < TARGET_PICKS) {
      this.showPointerOverNextMushroom();
    }
  }

  // ─── Picking a random, evenly-spaced slot on the pizza ──────────────
  get pizzaItemRadius() { return 28; }

  pickPizzaSlot() {
    const r = this.pizzaItemRadius;
    const INNER_FRAC = 0.70;
    const maxRadius = Math.max(0, (PIZZA_DISPLAY_SIZE / 2) * INNER_FRAC - r);

    const placed = this.placedIngredients
      .filter(s => s && s.active !== false)
      .map(s => ({ x: s.x, y: s.y }));

    const sample = () => {
      const u = Math.random();
      const v = Math.random();
      const rr = maxRadius * Math.sqrt(u);
      const a  = 2 * Math.PI * v;
      return { x: PIZZA_X + Math.cos(a) * rr, y: PIZZA_Y + Math.sin(a) * rr };
    };

    const minDistanceTo = (p) => {
      let best = Infinity;
      for (let i = 0; i < placed.length; i++) {
        const dx = placed[i].x - p.x;
        const dy = placed[i].y - p.y;
        const d  = Math.sqrt(dx * dx + dy * dy);
        if (d < best) best = d;
      }
      return best;
    };

    let chosen;
    if (placed.length === 0) {
      // First item on a mid-ring (off-centre) so later picks can balance.
      const ringFrac = 0.45 + Math.random() * 0.1;
      const angle    = Math.random() * Math.PI * 2;
      const rr       = maxRadius * ringFrac;
      chosen = {
        x: PIZZA_X + Math.cos(angle) * rr,
        y: PIZZA_Y + Math.sin(angle) * rr,
      };
    } else {
      const cx = placed.reduce((a, p) => a + p.x, 0) / placed.length;
      const cy = placed.reduce((a, p) => a + p.y, 0) / placed.length;
      const n  = placed.length;
      const BALANCE_W = 2 * r;
      const K = 40;
      let best = null;
      let bestScore = -Infinity;
      for (let i = 0; i < K; i++) {
        const p = sample();
        const spacing = minDistanceTo(p);
        const ncx = (cx * n + p.x) / (n + 1);
        const ncy = (cy * n + p.y) / (n + 1);
        const offset = Math.sqrt((ncx - PIZZA_X) ** 2 + (ncy - PIZZA_Y) ** 2);
        const score = spacing - BALANCE_W * (offset / maxRadius);
        if (score > bestScore) { best = p; bestScore = score; }
      }
      chosen = best || sample();
    }

    const angle = (Math.random() * 360) - 180;
    return { x: chosen.x, y: chosen.y, angle };
  }

  // ═══════════════════════════════════════════════════════════════════
  // Evaluate → celebration → handoff
  // ═══════════════════════════════════════════════════════════════════
  onEvaluate() {
    if (this.evaluating) return;
    if (this.pickedCount !== TARGET_PICKS) return;
    this.evaluating = true;

    if (this.evaluatePulse) this.evaluatePulse.pause();
    this.evaluateBtn.setScale(this.evaluateBtnHomeScale).disableInteractive();

    if (this.text4) {
      this.tweens.add({
        targets: this.text4, alpha: 0, duration: 200,
        onComplete: () => this.text4.destroy(),
      });
    }
    if (this.dim) {
      this.tweens.add({
        targets: this.dim, alpha: 0, duration: 300, ease: 'Sine.easeIn',
        onComplete: () => { if (this.dim) { this.dim.destroy(); this.dim = null; } },
      });
    }

    this.playConfetti(0);
    this.playCorrectCelebration(() => this.handOff());
  }

  // ═══════════════════════════════════════════════════════════════════
  // Celebration — belt-driven box reveal (port of GameScene)
  // ═══════════════════════════════════════════════════════════════════
  setupTopLidAnim() {
    if (!this.textures.exists('topLidAnim')) return;
    if (this.anims.exists('top-lid-close')) return;
    this.anims.create({
      key: 'top-lid-close',
      frames: this.anims.generateFrameNames('topLidAnim', {
        start: 0, end: 71, prefix: 'topLidAnim', suffix: '.png',
      }),
      frameRate: 30, repeat: 0,
    });
  }

  setupConfetti() {
    if (!this.textures.exists('confetti')) return;
    this.confettiSprite = this.add
      .sprite(STAGE_W / 2, STAGE_H / 2, 'confetti')
      .setScale(1.5).setOrigin(0.5)
      .setVisible(false).setDepth(D.confetti);
    if (!this.anims.exists('play')) {
      this.anims.create({
        key: 'play',
        frames: this.anims.generateFrameNames('confetti', {
          start: 0, end: 47, zeroPad: 0,
          prefix: 'checkconfetti', suffix: '.png',
        }),
        frameRate: 25, repeat: 0,
      });
    }
  }

  playConfetti(delay = 0) {
    if (!this.confettiSprite) return;
    this.time.delayedCall(delay, () => {
      this.confettiSprite.setVisible(true).setDepth(D.confetti);
      this.confettiSprite.play('play');
    });
  }

  playCorrectCelebration(onDone) {
    this.revealBoxByBelt(() => {
      this.boxBottom.setDepth(D.box);
      this.boxTop.setDepth(D.box + 2);
      this.flyPizzaIntoBox(this.boxTop, this.boxBottom, onDone);
    });
  }

  revealBoxByBelt(onComplete) {
    const distance = TRAY_OFFSCREEN_Y_BOTTOM - TRAY_HOME_Y;
    const duration = (distance / BELT_SCROLL_SPEED) * 1000;
    const trayTargets = [this.tray, ...this.trayIngredients].filter(Boolean);
    const boxTargets  = [this.boxBottom, this.boxTop].filter(Boolean);

    this.beltMoving = true;
    this.tweens.add({
      targets: [...trayTargets, ...boxTargets],
      y: '+=' + distance,
      duration, ease: 'Linear',
      onComplete: () => {
        this.beltMoving = false;
        trayTargets.forEach(s => s.destroy());
        this.tray = null;
        this.trayIngredients = [];
        onComplete();
      },
    });
  }

  flyPizzaIntoBox(top, bottom, onDone) {
    const TARGET = { x: BOX_HOME_X, y: BOX_HOME_Y };
    const FLY_MS = 450;
    const SHRINK = 360 / PIZZA_DISPLAY_SIZE;

    if (this.pizza) {
      this.pizza.setDepth(D.box + 1);
      this.tweens.add({
        targets: this.pizza,
        x: TARGET.x, y: TARGET.y,
        displayWidth:  PIZZA_DISPLAY_SIZE * SHRINK,
        displayHeight: PIZZA_DISPLAY_SIZE * SHRINK,
        duration: FLY_MS, ease: 'Cubic.easeIn',
      });
    }
    this.placedIngredients.forEach(s => {
      s.setDepth(D.box + 1);
      const ox = (s.x - PIZZA_X) * SHRINK;
      const oy = (s.y - PIZZA_Y) * SHRINK;
      const w  = (s.displayWidth  || 48) * SHRINK;
      const h  = (s.displayHeight || 48) * SHRINK;
      this.tweens.add({
        targets: s,
        x: TARGET.x + ox, y: TARGET.y + oy,
        displayWidth: w, displayHeight: h,
        duration: FLY_MS, ease: 'Cubic.easeIn',
      });
    });

    this.time.delayedCall(FLY_MS, () => {
      top.play('top-lid-close');
      top.once('animationcomplete', () => this.sendBoxOffRight(top, bottom, onDone));
    });
  }

  sendBoxOffRight(top, bottom, onDone) {
    const distance = (config.width + 600) - bottom.x;
    const targets = [top, bottom];
    if (this.pizza) targets.push(this.pizza);
    targets.push(...this.placedIngredients);
    this.tweens.add({
      targets, x: '+=' + distance,
      duration: 1100, ease: 'Cubic.easeIn',
      onComplete: () => {
        top.destroy(); bottom.destroy();
        this.placedIngredients.forEach(s => s.destroy());
        this.placedIngredients = [];
        this.boxBottom = null;
        this.boxTop = null;
        if (onDone) onDone();
      },
    });
  }

  handOff() {
    this.cameras.main.fadeOut(500, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      // Tutorial is teaching, not a scored round — hand off with score 0.
      this.scene.start('GameScene', { startingScore: 0 });
    });
  }
}
